import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { BeatsSortBy } from '../../../enums/enums'
import { IBeatDictionaryItem } from '../../../Interface/beat-dictionary'
import { ITrack } from '../../../Interface/track'
import { GlobalSearchService, SearchResponseType } from '../../../services/global-search.service'
import { I18nService, LgType } from '../../../services/prod/i18n.service'
import { TracksService } from '../../../services/tracks.service'
import {
  FilterPropertyType,
  FindTrackType,
  IdType,
  MultiSelectMode,
  SelectedTagType
} from '../../../types/filter.type'

@Component({
  selector: 'bmcore-find-tracks',
  templateUrl: './bmcore-find-tracks.component.html',
  styleUrls: ['./bmcore-find-tracks.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BmcoreFindTracksComponent implements OnInit {
  @Input() beatmakerId: number
  @Input() styleId: IdType
  @Input() moodId: IdType
  @Input() searchTagTitle: string
  @Input() searchTrack: string
  @Input() searchBeatmakerTitle: string
  @Input() currentSortById: BeatsSortBy
  @Input() isBeatmakerPage: boolean
  @Input() isFreeBeatsPage = false
  @Input() showSearchFields = true

  /** расширенные фильтры каталога: у фри-битов и виджета их нет */
  @Input() showCatalogFilters = false
  @Input() selectedTags: SelectedTagType[] = []
  @Input() tagsMode: MultiSelectMode = 'and'
  @Input() instrumentIds: number[] = []
  @Input() instrumentsMode: MultiSelectMode = 'and'
  @Input() keyId: IdType = 'all'
  @Input() trackTypeId: IdType = 'all'
  @Input() bpmFrom: number = null
  @Input() bpmTo: number = null
  @Input() priceFrom: number = null
  @Input() priceTo: number = null
  @Input() hasActiveFilters = false

  @Output() filterTracksEvent = new EventEmitter<FilterPropertyType>()

  lg: LgType
  tracks: Array<ITrack> = []

  /** На мобильных фильтры свёрнуты по умолчанию, раскрываются по тапу */
  filtersOpen = false

  isTagDropdown = false
  isBeatmakerDropdown = false
  searchResponse: SearchResponseType[] = []

  keys: IBeatDictionaryItem[] = []
  instruments: IBeatDictionaryItem[] = []
  trackTypes: IBeatDictionaryItem[] = []

  /* Debounce https://stackoverflow.com/a/40777621 */
  searchValueSubject = new Subject<FindTrackType>()

  constructor(
    private i18nService: I18nService,
    private globalSearchService: GlobalSearchService,
    private tracksService: TracksService
  ) {}

  ngOnInit() {
    this.lg = this.i18nService.getLanguageForPage()
    this.debounceSearchValue()

    this.tracksService.Keys.subscribe(res => (this.keys = res))
    this.tracksService.Instruments.subscribe(res => (this.instruments = res))
    this.tracksService.TrackTypes.subscribe(res => (this.trackTypes = res))
  }

  toggleFilters() {
    this.filtersOpen = !this.filtersOpen
  }

  /** Количество активных фильтров — показываем бейджем на кнопке «Фильтры» */
  get activeFilterCount(): number {
    let count = 0

    if (this.styleId && this.styleId !== 'all') count++
    if (this.moodId && this.moodId !== 'all') count++
    if (this.searchTagTitle) count++
    if (this.searchTrack) count++
    if (this.searchBeatmakerTitle) count++
    if (this.selectedTags?.length) count++

    if (this.showCatalogFilters) {
      if (this.instrumentIds?.length) count++
      if (this.keyId && this.keyId !== 'all') count++
      if (this.trackTypeId && this.trackTypeId !== 'all') count++
      if (this.bpmFrom != null || this.bpmTo != null) count++
      if (this.priceFrom != null || this.priceTo != null) count++
    }

    return count
  }

  removeTag(tagId: number) {
    this.filterTracksEvent.emit({
      title: 'tags',
      ids: this.selectedTags.filter(tag => tag.id !== tagId).map(tag => tag.id),
      mode: this.tagsMode
    })
  }

  changeTagsMode(mode: MultiSelectMode) {
    this.filterTracksEvent.emit({
      title: 'tags',
      ids: this.selectedTags.map(tag => tag.id),
      mode
    })
  }

  clearFilters() {
    this.searchTagTitle = ''
    this.searchTrack = ''
    this.searchBeatmakerTitle = ''
    this.filterTracksEvent.emit({ title: 'clear' })
  }

  /* Debounce https://stackoverflow.com/a/40777621 */
  debounceSearchValue() {
    this.searchValueSubject.pipe(debounceTime(700), distinctUntilChanged()).subscribe(model => {
      switch (model.searchType) {
        case 'name':
          this.searchTrack = model.search
          this.filterTracksEvent.emit({ title: model.searchType, search: model.search })
          break

        case 'tag':
          this.searchTagTitle = model.search
          if (this.searchTagTitle === '') {
            this.filterTracksEvent.emit({ title: 'tag', id: null })
            this.showTagDropdown(false)
          } else {
            this.globalSearchService.getTagIdBySearch(this.searchTagTitle).subscribe(res => {
              this.showTagDropdown(true)
              this.searchResponse = res
            })
          }
          break

        case 'beatmaker':
          this.searchBeatmakerTitle = model.search
          if (this.searchBeatmakerTitle === '') {
            this.filterTracksEvent.emit({ title: 'beatmaker', id: null })
            this.showBeatmakerDropdown(false)
          } else {
            this.globalSearchService.getBeatmakerIdBySearch(this.searchBeatmakerTitle).subscribe(res => {
              this.showBeatmakerDropdown(true)
              this.searchResponse = res
            })
          }
          break
      }
    })
  }

  searchValueChangeHandler(searchData: FindTrackType) {
    this.searchValueSubject.next({ search: searchData.search, searchType: searchData.searchType })
  }

  findTracksByTag(search: SearchResponseType) {
    // в каталоге теги накапливаются, на остальных страницах остаётся прежнее поведение
    if (!this.showCatalogFilters) {
      this.searchTagTitle = search.value
      this.filterTracksEvent.emit({ title: 'tag', id: search.id, search: search.value })

      return
    }

    this.searchTagTitle = ''

    if (this.selectedTags.some(tag => tag.id === search.id)) {
      return
    }

    this.filterTracksEvent.emit({
      title: 'tags',
      ids: [...this.selectedTags.map(tag => tag.id), search.id],
      mode: this.tagsMode,
      search: search.value
    })
  }

  findTracksByBeatmakerName(search: SearchResponseType) {
    this.searchBeatmakerTitle = search.value
    this.filterTracksEvent.emit({ title: 'beatmaker', id: search.id, search: search.value })
  }

  showBeatmakerDropdown(value: boolean) {
    this.isBeatmakerDropdown = value
  }

  showTagDropdown(value: boolean) {
    this.isTagDropdown = value
  }

  clearSearchTagTitle() {
    this.searchTagTitle = ''
  }

  clearSearchBeatmakerTitle() {
    this.searchBeatmakerTitle = ''
  }
}
